
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { EventVenue } from "@/components/event-card/types";
import { sendRecommendationsAvailableNotification } from "@/services/notification";

export const useVenueShortlist = (eventId: string) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const queryClient = useQueryClient();

  // Use React Query to fetch and cache the shortlisted venues
  const { data: shortlistedVenues = [] } = useQuery({
    queryKey: ['venues', 'recommendations', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_venue_recommendations')
        .select(`
          recommendation_id,
          venue_id,
          venue:venues (
            venue_id,
            name,
            address,
            image_url,
            style,
            price_range
          )
        `)
        .eq('event_id', eventId);

      if (error) {
        console.error('Error fetching venue shortlist:', error);
        return [];
      }

      // Transform the data to match EventVenue format
      return data.map(item => ({
        ...item.venue,
        recommendation_id: item.recommendation_id
      })) as EventVenue[];
    }
  });

  // Fetch event details to get the host ID
  const { data: eventDetails } = useQuery({
    queryKey: ['event-host-details', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('host_user_id')
        .eq('event_id', eventId)
        .single();

      if (error) throw error;
      return data;
    }
  });

  // Mutation for sending notification
  const sendNotificationMutation = useMutation({
    mutationFn: async (hostId: string) => {
      const result = await sendRecommendationsAvailableNotification(eventId, hostId);
      return result;
    },
    onError: (error) => {
      console.error('Error sending recommendations notification:', error);
    }
  });

  // Check if we should send a notification when we have enough recommendations
  useEffect(() => {
    const checkAndSendNotification = async () => {
      // If we have at least 3 venue recommendations, send a notification to the host
      if (shortlistedVenues.length >= 3 && eventDetails?.host_user_id) {
        try {
          // Check if we also have date recommendations
          const { data: dateRecommendations, error } = await supabase
            .from('event_date_recommendations')
            .select('recommendation_id')
            .eq('event_id', eventId);
            
          if (error) throw error;
          
          // Only send notification if we have both date and venue recommendations
          if (dateRecommendations && dateRecommendations.length > 0) {
            sendNotificationMutation.mutate(eventDetails.host_user_id);
          }
        } catch (error) {
          console.error('Error checking date recommendations:', error);
        }
      }
    };
    
    checkAndSendNotification();
  }, [shortlistedVenues.length, eventDetails, eventId, sendNotificationMutation]);

  // Add venue to shortlist
  const addToShortlist = useCallback(async (venueId: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('event_venue_recommendations')
        .insert({
          event_id: eventId,
          venue_id: venueId
        });

      if (error) {
        console.error('Error adding venue to shortlist:', error);
        toast({
          title: "Error",
          description: "Failed to add venue to shortlist."
        });
        return;
      }
      
      // Invalidate query to refresh data
      queryClient.invalidateQueries({ queryKey: ['venues', 'recommendations', eventId] });
      toast({
        title: "Venue added",
        description: "The venue has been added to your shortlist."
      });
    } finally {
      setIsProcessing(false);
    }
  }, [eventId, queryClient]);

  // Remove venue from shortlist
  const removeFromShortlist = useCallback(async (venueId: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('event_venue_recommendations')
        .delete()
        .eq('event_id', eventId)
        .eq('venue_id', venueId);

      if (error) {
        console.error('Error removing venue from shortlist:', error);
        toast({
          title: "Error",
          description: "Failed to remove venue from shortlist."
        });
        return;
      }
      
      // Invalidate query to refresh data
      queryClient.invalidateQueries({ queryKey: ['venues', 'recommendations', eventId] });
      toast({
        title: "Venue removed",
        description: "The venue has been removed from your shortlist."
      });
    } finally {
      setIsProcessing(false);
    }
  }, [eventId, queryClient]);

  return {
    shortlistedVenues,
    addToShortlist,
    removeFromShortlist,
    isProcessing
  };
};
